import React, { useMemo } from 'react';
import { Checkbox, Radio, Typography } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { RadioChangeEvent } from 'antd/es/radio';
import { COMPETENCY_BY_ID } from '@evaltick/shared';
import { QUESTION_TYPE_LABELS } from '@/utils';
import type { InterviewTemplateItem, TemplateItemHistory } from '@/services/api';
import { EXHAUSTED_POOL_NOTES, isRecommendedRetry, lastAttemptByItemId } from './questionSelection';
import styles from './QuestionPicker.module.scss';

const { Text } = Typography;

interface QuestionPickerProps {
  items: InterviewTemplateItem[];
  /** Null on a first attempt — no scores, tags or Same/Different choice to show. */
  history: TemplateItemHistory | null;
  selectedIds: string[];
  differentIds: string[];
  onChange: (selectedIds: string[], differentIds: string[]) => void;
  /** Show the last-asked question under each answered row. */
  showQuestionText?: boolean;
  /** Extra class on the row list, e.g. to cap its height. */
  listClassName?: string;
}

/**
 * One numbered row per question, labelled with its competency — so a
 * competency asked twice shows twice. Used by the setup wizard's "Choose
 * competencies" step and the results page's Try Again.
 */
export const QuestionPicker: React.FC<QuestionPickerProps> = ({
  items,
  history,
  selectedIds,
  differentIds,
  onChange,
  showQuestionText = false,
  listClassName,
}) => {
  const lastAttempts = useMemo(() => lastAttemptByItemId(history?.itemScoreHistory), [history]);
  const selectedSet = new Set(selectedIds);
  const differentSet = new Set(differentIds);

  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const noneSelected = selectedIds.length === 0;

  // A Different-question pick only applies to a selected row, so it's dropped with it.
  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(
        selectedIds.filter((x) => x !== id),
        differentIds.filter((x) => x !== id)
      );
    } else {
      onChange([...selectedIds, id], differentIds);
    }
  };

  const toggleAll = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      onChange(items.map((item) => item.id), differentIds);
    } else {
      onChange([], []);
    }
  };

  const setWantsDifferent = (id: string, wantsDifferent: boolean) => {
    const others = differentIds.filter((x) => x !== id);
    onChange(selectedIds, wantsDifferent ? [...others, id] : others);
  };

  return (
    <div className={styles.picker}>
      <div className={styles.selectAllRow}>
        <Checkbox indeterminate={!allSelected && !noneSelected} checked={allSelected} onChange={toggleAll}>
          Select all
        </Checkbox>
        <Text type="secondary" className={styles.selectedCount}>
          {selectedIds.length} of {items.length} questions selected
        </Text>
      </div>

      <ol className={listClassName ? `${styles.list} ${listClassName}` : styles.list}>
        {items.map((item, index) => {
          const lastAttempt = lastAttempts.get(item.id);
          const isSelected = selectedSet.has(item.id);
          const wantsDifferent = differentSet.has(item.id);
          const availability = history?.substitutionAvailability[item.id];
          const exhaustedPoolNote =
            isSelected && wantsDifferent && availability ? EXHAUSTED_POOL_NOTES[availability] : undefined;
          return (
            <li key={item.id} className={isSelected ? `${styles.row} ${styles.rowSelected}` : styles.row}>
              <label className={styles.rowLabel}>
                <Checkbox checked={isSelected} onChange={() => toggle(item.id)} />
                <span className={styles.rowNumber}>{index + 1}</span>
                <span className={styles.rowBody}>
                  <Text className={styles.rowName}>
                    {COMPETENCY_BY_ID[item.competencyId]?.name ?? item.competencyId}
                  </Text>
                  <span className={styles.rowMeta}>
                    <Text className={styles.rowType}>{QUESTION_TYPE_LABELS[item.questionType]}</Text>
                    {history && !lastAttempt && <Text className={styles.untriedTag}>Not tried yet</Text>}
                    {isRecommendedRetry(lastAttempt) && <Text className={styles.recommendedTag}>Recommended</Text>}
                  </span>
                </span>
              </label>
              {lastAttempt?.overallScore != null && (
                <Text type="secondary" className={styles.rowScore}>
                  {Math.round(lastAttempt.overallScore)} / 100
                </Text>
              )}
              {isSelected && lastAttempt && (
                <Radio.Group
                  size="small"
                  className={styles.rowQuestionChoice}
                  value={wantsDifferent ? 'different' : 'same'}
                  onChange={(e: RadioChangeEvent) => setWantsDifferent(item.id, e.target.value === 'different')}
                >
                  <Radio.Button value="same">Same question</Radio.Button>
                  <Radio.Button value="different">Different question</Radio.Button>
                </Radio.Group>
              )}
              {/* lastAskedText, not the template text — an earlier retry may have swapped the question. */}
              {showQuestionText && lastAttempt && (
                <Text className={styles.rowText}>{lastAttempt.lastAskedText}</Text>
              )}
              {exhaustedPoolNote && <Text className={styles.rowPoolNote}>{exhaustedPoolNote}</Text>}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default QuestionPicker;
